import './improved_signup.css';

import { faCheckCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Divider, Input, Modal, Select } from 'antd';
import { useFormik } from 'formik';
import React, { useState } from 'react';

import colors from '../../../foundation/assets/colors';
import LogoDark from '../../../foundation/assets/svgs/LogoDark';
import axios from '../../../foundation/config/axios';
import env_constants from '../../../internals/env/env_constants.json';
import { ImprovedSignupFormValidationSchema } from './improved_signup_validation';

const { Option } = Select;

type ImprovedSignupModalProps = {
  /** Callback function to close the modal */
  onClose: () => void;
};

/**
 * ImprovedSignupModal Component
 *
 * Enhanced signup form that sends registration data directly to backend.
 * Backend sends email to admin automatically - no email client required.
 * Uses Formik for form management and Yup for validation.
 */
const ImprovedSignupModal = ({ onClose }: ImprovedSignupModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      institution: '',
      institutionAddress: '',
      academicDegree: '',
      fieldOfStudy: '',
      position: '',
      orcidId: '',
    },
    validationSchema: ImprovedSignupFormValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        // Send to backend - backend sends email automatically
        const response = await axios.post(
          `${env_constants.API_BASE_URL}/auth/registration/registration-request`,
          {
            first_name: values.firstName.trim(),
            last_name: values.lastName.trim(),
            email: values.email.toLowerCase().trim(),
            phone: values.phone?.trim() || '',
            institution: values.institution.trim(),
            academic_degree: values.academicDegree,
            field_of_study: values.fieldOfStudy,
            position: values.position.trim(),
            orcid_id: values.orcidId?.trim() || '',
          },
        );

        if (response.data?.success) {
          setSubmitSuccess(true);
        } else {
          setSubmitError(
            response.data?.message ||
              'The server returned an unsuccessful status.',
          );
        }
      } catch (error: any) {
        console.error('Registration request error:', error);
        setSubmitError(
          error.response?.data?.message ||
            error.message ||
            'Failed to submit registration request. Please try again or contact support.',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleModalClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Success Screen - shows after successful submission
  if (submitSuccess) {
    return (
      <Modal
        open
        footer={null}
        closable={false}
        maskClosable={false}
        width={600}
        centered
        className="l-improved-signup"
      >
        <div className="l-improved-signup__success">
          <div className="l-improved-signup__success-icon">
            <FontAwesomeIcon
              icon={faCheckCircle}
              size="3x"
              color={colors.success}
            />
          </div>
          <h2>Registration Request Submitted!</h2>
          <p>
            Thank you for your interest in DNAwiz. Your registration request has
            been successfully sent to our team.
          </p>
          <Alert
            message="What happens next?"
            description={
              <ul style={{ paddingLeft: 20, marginTop: 10 }}>
                <li>Our team will review your credentials</li>
                <li>You will receive an email once your account is created</li>
                <li>This usually takes 1-2 business days</li>
              </ul>
            }
            type="info"
            style={{ marginTop: 20, textAlign: 'left' }}
          />
          <Button
            type="primary"
            onClick={handleModalClose}
            size="large"
            style={{ marginTop: 20 }}
          >
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      footer={null}
      title={null}
      closable={false}
      maskClosable={!isSubmitting}
      onCancel={handleModalClose}
      width={720}
      centered
      className="l-improved-signup"
      wrapClassName="l-improved-signup__modal-wrapper"
      rootClassName="l-improved-signup__modal-root"
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', padding: 0 }}
    >
      <div className="l-improved-signup__container">
        {/* Modal Header */}
        <div className="l-improved-signup__header">
          <div className="l-improved-signup__logo">
            <LogoDark />
          </div>
          {!isSubmitting && (
            <div
              className="l-improved-signup__close"
              onClick={handleModalClose}
            >
              <FontAwesomeIcon icon={faTimes} color={colors.primary} />
            </div>
          )}
        </div>

        {/* Modal Content */}
        <div className="l-improved-signup__content">
          <h2 className="l-improved-signup__title">Registration for DNAwiz</h2>
          <p className="l-improved-signup__subtitle">
            Please fill out the form below to request access to DNAwiz.
            <br />
            Your request will be sent automatically to our team.
          </p>

          {submitError && (
            <Alert
              message="Submission Failed"
              description={submitError}
              type="error"
              showIcon
              closable
              onClose={() => setSubmitError(null)}
              style={{ marginBottom: 20 }}
            />
          )}

          <form onSubmit={formik.handleSubmit}>
            {/* Personal Information */}
            <Divider orientation="left">Personal Information</Divider>

            <div className="l-improved-signup__form-row">
              <div className="l-improved-signup__form-field">
                <label>
                  First Name <span className="required">*</span>
                </label>
                <Input
                  name="firstName"
                  placeholder="First Name"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  status={
                    formik.touched.firstName && formik.errors.firstName
                      ? 'error'
                      : ''
                  }
                  disabled={isSubmitting}
                />
                {formik.touched.firstName && formik.errors.firstName && (
                  <div className="l-improved-signup__error">
                    {formik.errors.firstName}
                  </div>
                )}
              </div>

              <div className="l-improved-signup__form-field">
                <label>
                  Last Name <span className="required">*</span>
                </label>
                <Input
                  name="lastName"
                  placeholder="Last Name"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  status={
                    formik.touched.lastName && formik.errors.lastName
                      ? 'error'
                      : ''
                  }
                  disabled={isSubmitting}
                />
                {formik.touched.lastName && formik.errors.lastName && (
                  <div className="l-improved-signup__error">
                    {formik.errors.lastName}
                  </div>
                )}
              </div>
            </div>

            <div className="l-improved-signup__form-field">
              <label>
                Email <span className="required">*</span>
              </label>
              <Input
                type="email"
                name="email"
                placeholder="your.email@institution.edu"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                status={
                  formik.touched.email && formik.errors.email ? 'error' : ''
                }
                disabled={isSubmitting}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="l-improved-signup__error">
                  {formik.errors.email}
                </div>
              )}
            </div>

            <div className="l-improved-signup__form-field">
              <label>Phone (optional)</label>
              <Input
                name="phone"
                placeholder="+49 30 12345678"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                status={
                  formik.touched.phone && formik.errors.phone ? 'error' : ''
                }
                disabled={isSubmitting}
              />
              {formik.touched.phone && formik.errors.phone && (
                <div className="l-improved-signup__error">
                  {formik.errors.phone}
                </div>
              )}
            </div>

            {/* Academic Information */}
            <Divider orientation="left">
              Academic & Professional Background
            </Divider>

            <div className="l-improved-signup__form-field">
              <label>
                Academic Degree <span className="required">*</span>
              </label>
              <Select
                placeholder="Select your academic degree"
                value={formik.values.academicDegree || undefined}
                onChange={(value) =>
                  formik.setFieldValue('academicDegree', value)
                }
                onBlur={() => formik.setFieldTouched('academicDegree', true)}
                status={
                  formik.touched.academicDegree && formik.errors.academicDegree
                    ? 'error'
                    : ''
                }
                disabled={isSubmitting}
                style={{ width: '100%' }}
              >
                <Option value="Bachelor">Bachelor</Option>
                <Option value="Master">Master</Option>
                <Option value="PhD">PhD / Doctorate</Option>
                <Option value="MD">MD</Option>
                <Option value="Professor">Professor</Option>
                <Option value="Post-Doc">Post-Doctoral Researcher</Option>
                <Option value="Other">Other</Option>
              </Select>
              {formik.touched.academicDegree &&
                formik.errors.academicDegree && (
                  <div className="l-improved-signup__error">
                    {formik.errors.academicDegree}
                  </div>
                )}
            </div>

            <div className="l-improved-signup__form-field">
              <label>
                Field of Study <span className="required">*</span>
              </label>
              <Select
                placeholder="Select your field of study"
                value={formik.values.fieldOfStudy || undefined}
                onChange={(value) =>
                  formik.setFieldValue('fieldOfStudy', value)
                }
                onBlur={() => formik.setFieldTouched('fieldOfStudy', true)}
                status={
                  formik.touched.fieldOfStudy && formik.errors.fieldOfStudy
                    ? 'error'
                    : ''
                }
                disabled={isSubmitting}
                style={{ width: '100%' }}
              >
                <Option value="Human Genetics">Human Genetics</Option>
                <Option value="Medical Genetics">Medical Genetics</Option>
                <Option value="Molecular Biology">Molecular Biology</Option>
                <Option value="Genomics">Genomics</Option>
                <Option value="Bioinformatics">Bioinformatics</Option>
                <Option value="Clinical Genetics">Clinical Genetics</Option>
                <Option value="Genetic Counseling">Genetic Counseling</Option>
                <Option value="Laboratory Medicine">Laboratory Medicine</Option>
                <Option value="Other">Other</Option>
              </Select>
              {formik.touched.fieldOfStudy && formik.errors.fieldOfStudy && (
                <div className="l-improved-signup__error">
                  {formik.errors.fieldOfStudy}
                </div>
              )}
            </div>

            <div className="l-improved-signup__form-field">
              <label>
                Position / Role <span className="required">*</span>
              </label>
              <Select
                placeholder="Select your position"
                value={formik.values.position || undefined}
                onChange={(value) => formik.setFieldValue('position', value)}
                onBlur={() => formik.setFieldTouched('position', true)}
                status={
                  formik.touched.position && formik.errors.position
                    ? 'error'
                    : ''
                }
                disabled={isSubmitting}
                style={{ width: '100%' }}
              >
                <Option value="Researcher">Researcher</Option>
                <Option value="Clinician">Clinician</Option>
                <Option value="Laboratory Technician">
                  Laboratory Technician
                </Option>
                <Option value="Genetic Counselor">Genetic Counselor</Option>
                <Option value="Professor/Lecturer">Professor/Lecturer</Option>
                <Option value="PhD Student">PhD Student</Option>
                <Option value="Master Student">Master Student</Option>
                <Option value="Post-Doctoral Fellow">
                  Post-Doctoral Fellow
                </Option>
                <Option value="Other">Other</Option>
              </Select>
              {formik.touched.position && formik.errors.position && (
                <div className="l-improved-signup__error">
                  {formik.errors.position}
                </div>
              )}
            </div>

            {/* Institution Information */}
            <Divider orientation="left">Institution</Divider>

            <div className="l-improved-signup__form-field">
              <label>
                Institution <span className="required">*</span>
              </label>
              <Input
                name="institution"
                placeholder="e.g., Charité - Universitätsmedizin Berlin"
                value={formik.values.institution}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                status={
                  formik.touched.institution && formik.errors.institution
                    ? 'error'
                    : ''
                }
                disabled={isSubmitting}
              />
              {formik.touched.institution && formik.errors.institution && (
                <div className="l-improved-signup__error">
                  {formik.errors.institution}
                </div>
              )}
            </div>

            <div className="l-improved-signup__form-field">
              <label>Institution Address (optional)</label>
              <Input
                name="institutionAddress"
                placeholder="Street, City, Country"
                value={formik.values.institutionAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isSubmitting}
              />
            </div>

            {/* Additional Information */}
            <Divider orientation="left">
              Additional Information (optional)
            </Divider>

            <div className="l-improved-signup__form-field">
              <label>ORCID iD</label>
              <Input
                name="orcidId"
                placeholder="0000-0000-0000-0000"
                value={formik.values.orcidId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                status={
                  formik.touched.orcidId && formik.errors.orcidId ? 'error' : ''
                }
                disabled={isSubmitting}
              />
              {formik.touched.orcidId && formik.errors.orcidId && (
                <div className="l-improved-signup__error">
                  {formik.errors.orcidId}
                </div>
              )}
              <div className="l-improved-signup__hint">
                Format: 0000-0000-0000-0000
              </div>
            </div>

            {/* Form Actions */}
            <div className="l-improved-signup__actions">
              <Button
                onClick={handleModalClose}
                disabled={isSubmitting}
                size="large"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                size="large"
              >
                Send Registration Request
              </Button>
            </div>
          </form>

          <div className="l-improved-signup__footer">
            <p>
              <strong>Note:</strong> After submitting, your request will be sent
              automatically to our team. No email client required!
            </p>
            <p>We will get back to you shortly.</p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ImprovedSignupModal;
